import React, { useEffect, useRef } from 'react';
import { getAudioFeedbackManager } from '@/utils/audioFeedback';
import { getVisualFeedbackManager } from '@/utils/visualFeedback';

export interface FeedbackConfig {
  visual: {
    enableBackgroundFlash: boolean;
    enableRadialWaves: boolean;
    flashDuration: number;
    waveDuration: number;
    useMotionAnimation: boolean; // 🆕 NEW: Animation style choice
  };
  audio: {
    enableSounds: boolean;
    correctSound: string;
    incorrectSound: string;
    volume: number;
  };
}

interface FeedbackSystemProps {
  config?: Partial<FeedbackConfig>;
  onCorrect?: () => void;
  onIncorrect?: () => void;
  children?: React.ReactNode;
}

export const FeedbackSystem: React.FC<FeedbackSystemProps> = ({
  config,
  onCorrect,
  onIncorrect,
  children
}) => {
  const audioManagerRef = useRef(getAudioFeedbackManager());
  const visualManagerRef = useRef(getVisualFeedbackManager());
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    // Update configurations if provided
    if (config?.audio) {
      audioManagerRef.current.updateConfig(config.audio);
    }
    if (config?.visual) {
      visualManagerRef.current.updateConfig(config.visual);
    }

    return () => {
      mountedRef.current = false;
      visualManagerRef.current.cleanup();
    };
  }, [config]);

  // Expose feedback methods through imperative handle or context
  const triggerCorrectFeedback = () => {
    if (!mountedRef.current) return;

    audioManagerRef.current.playCorrectSound();
    visualManagerRef.current.triggerCorrectFeedback();
    onCorrect?.();
  };

  const triggerCorrectSoundOnly = () => {
    if (!mountedRef.current) return;

    audioManagerRef.current.playCorrectSound();
    // No visual feedback, no onCorrect callback
  };

  const triggerIncorrectFeedback = () => {
    if (!mountedRef.current) return;

    audioManagerRef.current.playIncorrectSound();
    visualManagerRef.current.triggerIncorrectFeedback();
    onIncorrect?.();
  };

  const clearRedBackground = () => {
    if (!mountedRef.current) return;
    visualManagerRef.current.clearRedBackground();
  };

  // Make feedback methods available globally
  useEffect(() => {
    (window as any).keyboardHeroFeedback = {
      triggerCorrect: triggerCorrectFeedback,
      triggerCorrectSoundOnly: triggerCorrectSoundOnly,
      triggerIncorrect: triggerIncorrectFeedback,
      clearRed: clearRedBackground,
      isPersistentRed: () => visualManagerRef.current.isPersistentRed(),
      // 🆕 NEW: Easy animation testing
      switchAnimation: (useMotion: boolean) => {
        visualManagerRef.current.updateConfig({ useMotionAnimation: useMotion });
        console.log(`🎨 Animation switched to: ${useMotion ? 'MOTION (Smooth)' : 'ORIGINAL (Fixed)'}`);
      }
    };

    return () => {
      delete (window as any).keyboardHeroFeedback;
    };
  }, []);

  return (
    <>
      {children}
      <div
        id="feedback-system-container"
        style={{ display: 'none' }}
        data-testid="feedback-system"
      />
    </>
  );
};

// Utility functions for direct feedback control
export const triggerCorrectFeedback = () => {
  const feedback = (window as any).keyboardHeroFeedback;
  if (feedback) {
    feedback.triggerCorrect();
  }
};

export const triggerCorrectSoundOnly = () => {
  const feedback = (window as any).keyboardHeroFeedback;
  if (feedback) {
    feedback.triggerCorrectSoundOnly();
  }
};

export const triggerIncorrectFeedback = () => {
  const feedback = (window as any).keyboardHeroFeedback;
  if (feedback) {
    feedback.triggerIncorrect();
  }
};

export const clearRedBackground = () => {
  const feedback = (window as any).keyboardHeroFeedback;
  if (feedback) {
    feedback.clearRed();
  }
};

export const isPersistentRed = (): boolean => {
  const feedback = (window as any).keyboardHeroFeedback;
  return feedback ? feedback.isPersistentRed() : false;
};

export default FeedbackSystem;